/**
 * Subscription usage constants
 * Shared color coding thresholds: green <80%, yellow 80-94%, red 95-100%
 */

export const SUPPORT_EMAIL = 'support@transact.digital';

export const USAGE_THRESHOLDS = {
  WARNING: 80, // yellow at 80%
  CRITICAL: 95 // red at 95%
};

export const USAGE_COLORS = {
  NORMAL: 'green',
  WARNING: 'yellow',
  CRITICAL: 'red',
  // TD-2099 v3: enrichment uses grey "on hold" when exhausted, not red.
  // Aligned with Subscription Mgmt R4 (tokens + downloads on-hold treatment).
  HOLD: 'grey'
};

// TD-2099 v3: enrichment shows amber when balance drops below 20% of limit,
// then grey "on hold" at limit reached. Differs from downloads/tokens (80/95%).
export const ENRICHMENT_AMBER_THRESHOLD = 0.2;

/**
 * Returns the color status based on usage percentage
 * @param {number} used - Current usage count
 * @param {number} limit - Maximum limit
 * @returns {string} 'green' | 'yellow' | 'red'
 */
export const getUsageColor = (used, limit) => {
  if (!limit || limit <= 0) return USAGE_COLORS.NORMAL;
  const safeUsed = Number(used) || 0;
  const percentage = (safeUsed / limit) * 100;
  if (percentage >= USAGE_THRESHOLDS.CRITICAL) return USAGE_COLORS.CRITICAL;
  if (percentage >= USAGE_THRESHOLDS.WARNING) return USAGE_COLORS.WARNING;
  return USAGE_COLORS.NORMAL;
};

/**
 * Returns the usage percentage (capped at 100)
 * @param {number} used
 * @param {number} limit
 * @returns {number} 0-100
 */
export const getUsagePercentage = (used, limit) => {
  if (!limit || limit <= 0) return 0;
  const safeUsed = Number(used) || 0;
  return Math.min(Math.round((safeUsed / limit) * 100), 100);
};

/**
 * Returns remaining count
 * @param {number} used
 * @param {number} limit
 * @returns {number}
 */
export const getRemaining = (used, limit) => {
  if (!limit || limit <= 0) return 0;
  const safeUsed = Number(used) || 0;
  return Math.max(limit - safeUsed, 0);
};

/**
 * Checks if usage limit has been reached
 * @param {number} used
 * @param {number} limit
 * @returns {boolean}
 */
export const isLimitReached = (used, limit) => {
  if (!limit || limit <= 0) return false;
  const safeUsed = Number(used) || 0;
  return safeUsed >= limit;
};
